import { motion } from 'framer-motion';
import { getStageSubtitle } from '@/lib/transcriptionProgress';

interface ProgressViewProps {
  progress: number;
  status: string;
  title?: string;
  channelName?: string;
  isRetrying?: boolean;
  retryCount?: number;
}

export function ProgressView({
  progress,
  status,
  title,
  channelName,
  isRetrying,
  retryCount,
}: ProgressViewProps) {
  const subtitle = getStageSubtitle(status);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="w-full max-w-2xl mx-auto pt-2 md:py-8 px-4 space-y-6"
    >
      {/* Media info */}
      {title && (
        <div className="text-center space-y-1">
          <h1 className="text-xl font-semibold text-gray-900 line-clamp-2">{title}</h1>
          {channelName && <p className="text-sm text-gray-500">{channelName}</p>}
        </div>
      )}

      {/* Progress card */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="space-y-4">
          {/* Status and percentage */}
          <div className="flex justify-between items-start">
            <div className="min-w-0 flex-1">
              <span
                className="block text-sm font-medium uppercase tracking-wider text-gray-600"
                style={{ fontFamily: 'JetBrains Mono, monospace', letterSpacing: '0.1em' }}
              >
                {isRetrying ? `Retrying (${retryCount})...` : status}
              </span>
              <div className="h-5 mt-1">
                <span className="text-xs italic text-gray-400">{subtitle}</span>
              </div>
            </div>
            <span
              className="text-3xl font-bold tabular-nums shrink-0 text-gray-900"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}
              aria-live="polite"
              aria-atomic="true"
            >
              {Math.round(progress)}%
            </span>
          </div>

          {/* Progress bar with ARIA accessibility */}
          <div
            className="relative h-4 bg-gray-100 rounded-full overflow-hidden border border-gray-200"
            role="progressbar"
            aria-valuenow={Math.round(progress)}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Transcription progress: ${Math.round(progress)}%`}
          >
            <motion.div
              className="absolute top-0 left-0 h-full rounded-full"
              style={{
                background: `linear-gradient(90deg, #0066FF ${progress}%, #00C9FF ${Math.min(progress + 20, 100)}%)`,
              }}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
            />
          </div>

          {/* Processing animation */}
          <motion.div
            className="flex justify-center gap-2 pt-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            aria-hidden="true"
          >
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-2 h-2 rounded-full bg-blue-500"
                animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </motion.div>
        </div>
      </div>

      {/* Tips */}
      <p className="text-center text-xs text-gray-400">
        You can close this page — we'll save the transcript to your library automatically.
      </p>
    </motion.div>
  );
}
